/**
 * F 390 — THE GRAPH'S BEARER TOKEN, AND THE ONE FETCH EVERYBODY USES.
 *
 * The FCC graph (`/api/fcc/kv`) is now AUTHENTICATED on the server. It was
 * world-readable AND world-writable — measured, not assumed: an
 * unauthenticated GET returned graph data, an unauthenticated PUT applied a
 * probe key, and a wrong bearer token was ignored. F 390 closes that door.
 *
 * WHICH MEANS THE APP MUST PRESENT A TOKEN
 * -----------------------------------------
 * `RemoteState` is the app's single graph path, and it sent no
 * `Authorization` header at all. A server that now answers 401 to an
 * anonymous caller would have left the app with an empty workspace and a
 * localStorage-only fallback — the app would look like it had lost its data.
 *
 * So the app presents `VITE_FC_KV_TOKEN`.
 *
 * WHAT THAT BUYS, AND WHAT IT DOES NOT — stated here, because the distinction
 * matters more than the feature
 * ---------------------------------------------------------------
 * The token is a BUILD-TIME value, so it is readable in the public bundle.
 * That means:
 *
 *   - it DOES close the anonymous door: an unauthenticated caller now gets
 *     401, and an unauthenticated WRITE — which could overwrite the app's
 *     state or replace the credential node — is refused;
 *   - it does NOT make the graph confidential against someone who reads the
 *     bundle. They can extract the token and do what an authenticated caller
 *     does.
 *
 * What actually protects the SECRET in the graph is not this token: it is
 * that the credential is sealed by the app's own `CredentialStore` with the
 * user's PASSPHRASE, which is not in the bundle and which the app never
 * sends to the server. A bundle-reader gets ciphertext.
 *
 * The stronger posture — a token the operator enters once, device-locally,
 * with no server-side identity to check it against — needs F 374's local
 * unlock to become a real login. That is the follow-up, and it is named
 * rather than pretended away.
 *
 * FAIL CLOSED, AND SAY WHY
 * -------------------------
 * A build with no token would silently lose the graph. So the token's absence
 * is a named, surfaced condition (`graphAuthProblem()`), not a shrug.
 */

/** The build-time token. Absent in a build that was not provisioned. */
function readToken(): string {
  const env =
    typeof import.meta !== 'undefined'
      ? (import.meta as unknown as { env?: Record<string, string | undefined> }).env
      : undefined;
  const t = env?.VITE_FC_KV_TOKEN;
  return typeof t === 'string' ? t : '';
}

let cachedToken: string | null = null;

export function graphToken(): string {
  if (cachedToken === null) cachedToken = readToken();
  return cachedToken;
}

/** Why the app cannot reach the graph, or null when it can. */
export function graphAuthProblem(): string | null {
  if (graphToken()) return null;
  return (
    'This build carries no graph access token (VITE_FC_KV_TOKEN), so the FCC graph ' +
    'is unreachable and the app is running on local storage alone. The build must be ' +
    'provisioned with the same token the server holds in FCC_KV_TOKEN.'
  );
}

/**
 * The headers every graph request carries.
 *
 * A getter rather than a constant so a test can provision a token and the
 * module can cache it lazily without the module having to be re-imported.
 */
export function graphAuthHeaders(): Record<string, string> {
  const token = graphToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * The ONE graph fetch.
 *
 * Every caller goes through this, so there is exactly one place that knows
 * the graph needs a token. `RemoteState` and the lane graph both use it.
 */
export async function graphFetch(
  endpoint: string,
  path: string,
  init?: RequestInit,
): Promise<Response | null> {
  try {
    return await fetch(endpoint + path, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...graphAuthHeaders(),
        ...(init?.headers || {}),
      },
    });
  } catch {
    // Endpoint absent (static deploy) or offline. Degrade, never throw.
    return null;
  }
}
